import { useEffect, useState } from "react";
import {
  Alert,
  Autocomplete,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import FancyDialogActions from "./FancyDialogActions";
import { useApp } from "../../hooks/useApp";
import { getRequestWarehouse } from "../../services/location/LocationService";
import {
  getRoleWarehouses,
  saveRoleWarehouses,
} from "../../services/role/RoleWarehouseService";

interface WarehouseOption {
  id: number;
  name: string;
}

interface DialogProps {
  role: { id: number; roleName: string };
  cancelModal: () => void;
}

// El Administrador siempre trabaja con todas las bodegas (INVENTORY_API ignora cualquier asignación).
const ADMINISTRATOR_ROLE_ID = 1;

const RoleWarehousesModal = ({ role, cancelModal }: DialogProps) => {
  const { authInfo, setLoading, setErrorMsg, setSuccessMsg } = useApp();
  const [options, setOptions] = useState<WarehouseOption[]>([]);
  const [selected, setSelected] = useState<WarehouseOption[]>([]);
  const isAdministrator = role.id === ADMINISTRATOR_ROLE_ID;

  useEffect(() => {
    const load = async () => {
      try {
        setLoading && setLoading(true);
        const [warehouses, assigned]: any[] = await Promise.all([
          getRequestWarehouse({ filter: "", page: 0, size: 1000 }),
          getRoleWarehouses(role.id),
        ]);
        const all: WarehouseOption[] = warehouses?.content ?? [];
        setOptions(all);
        setSelected(
          all.filter((warehouse) => assigned.warehouseIds.includes(warehouse.id))
        );
      } catch (error: any) {
        setErrorMsg &&
          setErrorMsg(
            error?.response?.data?.message ??
              error?.message ??
              "No se pudieron cargar las bodegas del perfil"
          );
        cancelModal();
      } finally {
        setLoading && setLoading(false);
      }
    };
    load();
    // eslint-disable-next-line
  }, [role.id]);

  const handleSave = async () => {
    try {
      setLoading && setLoading(true);
      await saveRoleWarehouses(
        role.id,
        selected.map((warehouse) => warehouse.id),
        authInfo?.username ?? ""
      );
      setSuccessMsg &&
        setSuccessMsg(
          selected.length === 0
            ? `El perfil ${role.roleName} trabaja con todas las bodegas`
            : `Bodegas del perfil ${role.roleName} guardadas`
        );
      cancelModal();
    } catch (error: any) {
      setErrorMsg &&
        setErrorMsg(
          error?.response?.data?.message ??
            error?.message ??
            "No se pudieron guardar las bodegas del perfil"
        );
    } finally {
      setLoading && setLoading(false);
    }
  };

  return (
    <>
      <DialogTitle id="role-warehouses-title" sx={{ pt: 4, px: 4 }}>
        {`Bodegas con acceso: ${role.roleName}`}
      </DialogTitle>
      <DialogContent sx={{ px: 4, pb: 0 }}>
        {isAdministrator ? (
          <Alert severity="info" sx={{ mt: 2, mb: 6 }}>
            El Administrador siempre trabaja con todas las bodegas.
          </Alert>
        ) : (
          <>
            <Typography variant="body2" sx={{ mt: 2, mb: 3 }}>
              Las personas con este perfil sólo podrán consultar y trabajar con
              las bodegas que elija aquí. Si no elige ninguna, el perfil trabaja
              con todas las bodegas.
            </Typography>
            <Autocomplete
              multiple
              fullWidth
              size="small"
              options={options}
              value={selected}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              onChange={(_event, value) => setSelected(value)}
              noOptionsText="No hay bodegas"
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Bodegas"
                  placeholder={selected.length === 0 ? "Todas las bodegas" : ""}
                  InputLabelProps={{ shrink: true }}
                />
              )}
              sx={{ mb: 8 }}
            />
          </>
        )}
      </DialogContent>
      {isAdministrator ? (
        <FancyDialogActions
          handleAccept={cancelModal}
          cancelModal={cancelModal}
          textAcceptButton="Cerrar"
          textCancelButton="Cancelar"
        />
      ) : (
        <FancyDialogActions
          handleAccept={handleSave}
          cancelModal={cancelModal}
          textAcceptButton="Guardar"
        />
      )}
    </>
  );
};

export default RoleWarehousesModal;
